/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Activity } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import type { TrendDataPoint } from '../types'

interface TrafficChartProps {
  data: TrendDataPoint[]
  loading: boolean
  range: '24h' | '7d' | '30d'
}

export function TrafficChart({ data, loading, range }: TrafficChartProps) {
  const { t } = useTranslation()

  return (
    <div className='overflow-hidden rounded-xl border bg-card/50 backdrop-blur-sm'>
      <div className='flex items-center gap-2 border-b px-4 py-3 sm:px-5 sm:py-4'>
        <Activity className='text-muted-foreground/60 size-4' aria-hidden='true' />
        <div className='text-sm font-semibold'>{t('Real-time Traffic')}</div>
        <span className='text-muted-foreground text-xs'>
          {range === '24h'
            ? t('Last 24 hours')
            : range === '7d'
              ? t('Last 7 days')
              : t('Last 30 days')}
        </span>
      </div>

      <div className='h-[280px] p-3 sm:h-[320px] sm:p-4'>
        {loading ? (
          <div className='flex h-full flex-col gap-3'>
            <Skeleton className='h-full w-full' />
          </div>
        ) : data.length === 0 ? (
          <div className='text-muted-foreground flex h-full items-center justify-center text-xs'>
            {t('No data available')}
          </div>
        ) : (
          <ResponsiveContainer width='100%' height='100%'>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id='trafficGradient' x1='0' y1='0' x2='0' y2='1'>
                  <stop offset='0%' stopColor='hsl(var(--primary))' stopOpacity={0.25} />
                  <stop offset='100%' stopColor='hsl(var(--primary))' stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray='3 3'
                stroke='hsl(var(--border))'
                opacity={0.5}
                vertical={false}
              />
              <XAxis
                dataKey='label'
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickLine={false}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                interval='preserveStartEnd'
                minTickGap={24}
              />
              <YAxis
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={48}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null
                  return (
                    <div className='border-border/50 bg-background grid min-w-32 items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl'>
                      <div className='font-medium'>{label}</div>
                      <div className='flex items-center gap-2'>
                        <div className='h-2 w-2 shrink-0 rounded-sm bg-primary' />
                        <span className='text-muted-foreground'>
                          {t('Calls')}
                        </span>
                        <span className='text-foreground font-mono font-medium tabular-nums'>
                          {payload[0].value as number}
                        </span>
                      </div>
                    </div>
                  )
                }}
              />
              <Area
                type='monotone'
                dataKey='value'
                stroke='hsl(var(--primary))'
                strokeWidth={2}
                fill='url(#trafficGradient)'
                dot={false}
                activeDot={{
                  r: 4,
                  stroke: 'hsl(var(--background))',
                  strokeWidth: 2,
                  fill: 'hsl(var(--primary))',
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
